import { PresetTag, SoftTag } from '../../components/Tag/SoftTag';
import {
  CalendarOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  PauseOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { L } from '@lib';
import { BLUE, DANGER, GREEN, GREY, YELLOW } from '../../components/Theme/palette';
import React from 'react';

// "To do", "Ready to go" and "Estimate approved" were one thing - a job ready to be worked - and
// are now one status, `Booked` (the RenameOrderStatusToBooked migration moves the data). The three
// old keys stay in the maps below only so that an old history entry ("changed from Todo to ...")
// or a row the migration has not reached yet still reads as Booked; nothing offers them any more.
//
// Likewise "Parts not ordered", "Parts ordered" and "On hold": a job that is blocked and waiting for
// something is one status, `OnHold` (the MergeBlockedOrderStatusesToOnHold migration moves the
// data), and the two parts keys are legacy ones too.
export const LocalizedServiceOrderStatus = {
  Booked: L('Booked'),
  InProgress: L('In progress'),
  Approval: L('Estimate'),
  OnHold: L('On hold'),
  // Legacy (see above).
  Todo: L('Booked'),
  ReadyToGo: L('Booked'),
  Approved: L('Booked'),
  PartsNotOrdered: L('On hold'),
  PartsOrdered: L('On hold'),
  Canceled: L('Cancelled'),
  Completed: L('Completed'),
  Ready: L('Ready for payment'),
  Paid: L('Paid'),
};

export const OrderStatus = {
  Booked: 'Booked',
  InProgress: 'InProgress',
  Approval: 'Approval',
  OnHold: 'OnHold',
  Canceled: 'Canceled',
  Completed: 'Completed',
  Ready: 'Ready',
  Paid: 'Paid',
};

/**
 * The statuses a job can have while it waits in the side panel - open work, mirroring
 * OrderStatus.PendingScheduling on the API, the only ones GetAllUnassignedAsync returns. Anything
 * else (finished, cancelled) and the card comes off the panel, whether the change arrived by push
 * or by a save here.
 */
export const PENDING_SCHEDULING_STATUSES: string[] = [
  OrderStatus.Booked,
  OrderStatus.Approval,
  OrderStatus.InProgress,
  OrderStatus.OnHold,
];

/**
 * The statuses the side panel offers as filters: what a job that is not on the calendar can be.
 * (A job back on the panel is Booked, On hold, or an Estimate out for approval.)
 */
export const PANEL_STATUS_FILTERS: string[] = [
  OrderStatus.Booked,
  OrderStatus.OnHold,
  OrderStatus.Approval,
];

export const DropOffDateStatus = {
  Booked: 'Booked',
  OnHold: 'OnHold',
  // Legacy values a row can still have until the status migrations have run.
  Todo: 'Todo',
  ReadyToGo: 'ReadyToGo',
  PartsNotOrdered: 'PartsNotOrdered',
  PartsOrdered: 'PartsOrdered',
};

export const ServiceOrderInvoiceStatus = {
  Ready: 'Ready',
  Paid: 'Paid',
};
// The pale colour a status has in tables, the order view and the side panel's filter: the groups of
// the calendar (light blue: booked, in progress; light yellow: estimates, on hold; light green: done,
// awaiting payment; light grey: paid), plus red for cancelled.
const PALE_BLUE = BLUE.card;
const PALE_YELLOW = YELLOW.card;
const PALE_GREEN = GREEN.card;
const PALE_GREY = GREY.card;
const PALE_RED = DANGER.card;

export const ServiceOrderStatusColor = {
  Booked: PALE_BLUE,
  Approval: PALE_YELLOW,
  OnHold: PALE_YELLOW,
  InProgress: PALE_BLUE,
  Completed: PALE_GREEN,
  Ready: PALE_GREEN,
  Paid: PALE_GREY,
  Canceled: PALE_RED,
  // Legacy keys (see LocalizedServiceOrderStatus).
  Todo: PALE_BLUE,
  ReadyToGo: PALE_BLUE,
  Approved: PALE_BLUE,
  PartsNotOrdered: PALE_YELLOW,
  PartsOrdered: PALE_YELLOW,
};

// Calendar cards. Colours by group - blue (booked, in progress), yellow (estimates, on hold), green (done,
// awaiting payment), grey (paid) - plus red for cancelled. Three strengths
// of each, so the group reads at a glance without the board turning into a wall of colour:
//   badge - strong: the left stripe and the icon
//   card  - pale: the card's outline and header band
//   soft  - barely there (the pale tint at ~45%): a compact card's background
// The status tag is its own colour (`tag`): the group's, or red where the job needs somebody to
// do something about it (an estimate to approve).
// `main`: the group's own strong colour, for things that belong to the card rather than to the
// status tag (which is red on an Estimate, whose card is yellow) - e.g. the side panel's Details button.
const CALENDAR_BLUE = { card: BLUE.card, soft: BLUE.soft, badge: BLUE.badge, main: BLUE.tag };
const CALENDAR_YELLOW = { card: YELLOW.card, soft: YELLOW.soft, badge: YELLOW.badge, main: YELLOW.tag };
const CALENDAR_GREEN = { card: GREEN.card, soft: GREEN.soft, badge: GREEN.badge, main: GREEN.tag };
const CALENDAR_GREY = { card: GREY.card, soft: GREY.soft, badge: GREY.badge, main: GREY.tag };
const CALENDAR_RED = { card: DANGER.card, soft: DANGER.soft, badge: DANGER.badge, main: DANGER.tag };

const TAG_BLUE = BLUE.tag;
// The red group's strong colour is the theme's danger red (palette.ts).
const TAG_RED = DANGER.tag;
const TAG_YELLOW = YELLOW.tag;
const TAG_GREEN = GREEN.tag;
const TAG_GREY = GREY.tag;

export const CalendarStatusStyle = {
  Booked: { ...CALENDAR_BLUE, tag: TAG_BLUE, label: 'Booked' },
  Approval: { ...CALENDAR_YELLOW, tag: TAG_RED, label: 'Estimate' },
  OnHold: { ...CALENDAR_YELLOW, tag: TAG_YELLOW, label: 'On hold' },
  InProgress: { ...CALENDAR_BLUE, tag: TAG_BLUE, label: 'In progress' },
  Completed: { ...CALENDAR_GREEN, tag: TAG_GREEN, label: 'Completed' },
  Ready: { ...CALENDAR_GREEN, tag: TAG_GREEN, label: 'Ready for payment' },
  Paid: { ...CALENDAR_GREY, tag: TAG_GREY, label: 'Paid' },
  Canceled: { ...CALENDAR_RED, tag: TAG_RED, label: 'Cancelled' },
  // Legacy keys (see LocalizedServiceOrderStatus).
  Todo: { ...CALENDAR_BLUE, tag: TAG_BLUE, label: 'Booked' },
  ReadyToGo: { ...CALENDAR_BLUE, tag: TAG_BLUE, label: 'Booked' },
  Approved: { ...CALENDAR_BLUE, tag: TAG_BLUE, label: 'Booked' },
  PartsNotOrdered: { ...CALENDAR_YELLOW, tag: TAG_YELLOW, label: 'On hold' },
  PartsOrdered: { ...CALENDAR_YELLOW, tag: TAG_YELLOW, label: 'On hold' },
};
export type CalendarStatusStyleObjectKey = keyof typeof CalendarStatusStyle;

/**
 * An estimate that is out with the customer, waiting for them: nothing on the job can be edited until it is answered.
 * A draft estimate has not been sent yet - it is still a sketch and edited like any other job.
 */
export const isEstimateOut = (order?: { status?: string; isDraft?: boolean } | null) =>
  order?.status === 'Approval' && !order.isDraft;

/** Invoiced: the job is closed, and nothing on it can be edited. */
const LOCKED_STATUSES: string[] = [OrderStatus.Ready, OrderStatus.Paid];

/** Work starts, or picks up again, from these. */
export const STARTABLE_STATUSES: string[] = [OrderStatus.Booked, OrderStatus.OnHold];

/** The work can be finished from these. */
export const COMPLETABLE_STATUSES: string[] = [OrderStatus.Booked, OrderStatus.InProgress, OrderStatus.OnHold];

/** Past cancelling, or cancelled already. */
const NOT_CANCELLABLE_STATUSES: string[] = [...LOCKED_STATUSES, OrderStatus.Canceled];

type OrderVehicleFields = { vin?: string; registrationNumber?: string };

export const isOrderLocked = (status?: string) => !!status && LOCKED_STATUSES.includes(status);

export const orderHasVehicle = ({ vin, registrationNumber }: OrderVehicleFields) => !!vin || !!registrationNumber;

/**
 * A completed job with no vehicle (a voided QuickPay sale, say) has nothing to move back to, so it leaves
 * Completed only by being invoiced again or cancelled.
 */
export const isCompletedWithoutVehicle = (order: OrderVehicleFields, status?: string) =>
  status === OrderStatus.Completed && !orderHasVehicle(order);

/** A completed job with a vehicle stays protected from cancelling, even after its invoice is voided. */
export const canCancelOrder = (order: OrderVehicleFields, status?: string) => {
  if (!status || NOT_CANCELLABLE_STATUSES.includes(status)) return false;
  return status !== OrderStatus.Completed || !orderHasVehicle(order);
};

/**
 * A draft has the colours of what it is (a booking blue, an estimate amber with a red tag); only its tag says it is a
 * draft - "Draft Booking" / "Draft Estimate", as in the job card's Status row.
 */
export const draftTagLabel = (status?: string) => (status === OrderStatus.Approval ? 'Draft Estimate' : 'Draft Booking');

/**
 * A small icon for the status tag on the calendar cards, so a status reads by shape as well as by
 * colour (the colour groups share hues: Estimate and On hold are both yellow). Deliberately tiny -
 * it sits inside the tag, not beside it. In progress has none: its tag carries the running clock.
 */
export const CalendarStatusIcon: Partial<Record<CalendarStatusStyleObjectKey, React.ReactNode>> = {
  Booked: <CalendarOutlined />,
  Approval: <FileTextOutlined />,
  OnHold: <PauseOutlined />,
  Completed: <CheckOutlined />,
  Ready: <WalletOutlined />,
  Paid: <CheckCircleFilled />,
  Canceled: <CloseOutlined />,
};

// Left-hand hint icon on a calendar card (hover for the job's details). In the status colour the
// card sets as --status-stripe (see statusCardStyle); grey where there is none.
const calendarHintIcon = (
  <CheckCircleFilled style={{ fontSize: 18, color: 'var(--status-stripe, rgba(0, 0, 0, 0.65))' }} />
);
export const ServiceOrderStatusIcon = {
  Booked: calendarHintIcon,
  Approval: calendarHintIcon,
  OnHold: calendarHintIcon,
  Completed: calendarHintIcon,
  Ready: calendarHintIcon,
  Paid: calendarHintIcon,
  Canceled: calendarHintIcon,
  // Legacy keys (see LocalizedServiceOrderStatus).
  InProgress: calendarHintIcon,
  Todo: calendarHintIcon,
  ReadyToGo: calendarHintIcon,
  Approved: calendarHintIcon,
  PartsNotOrdered: calendarHintIcon,
  PartsOrdered: calendarHintIcon,
};

const tagStyle = { minWidth: 200, textAlign: 'center' as const };

// Tag colours follow the calendar's (CalendarStatusStyle.tag): blue for booked and in progress, red for
// an estimate, yellow on hold, green done, grey paid.
export const ServiceOrderStatusTag = {
  Booked: (
    <SoftTag group="blue" style={tagStyle}>
      Booked
    </SoftTag>
  ),
  Approval: (
    <SoftTag group="danger" style={tagStyle}>
      Estimate
    </SoftTag>
  ),
  OnHold: (
    <SoftTag group="yellow" style={tagStyle}>
      On hold
    </SoftTag>
  ),
  Completed: (
    <SoftTag group="green" style={tagStyle}>
      Completed
    </SoftTag>
  ),
  Ready: (
    <SoftTag group="green" style={tagStyle}>
      Ready for payment
    </SoftTag>
  ),
  Paid: (
    <PresetTag color="default" style={tagStyle}>
      Paid
    </PresetTag>
  ),
  Canceled: (
    <SoftTag group="danger" style={tagStyle}>
      Cancelled
    </SoftTag>
  ),
  // Legacy keys (see LocalizedServiceOrderStatus).
  InProgress: (
    <SoftTag group="blue" style={tagStyle}>
      In progress
    </SoftTag>
  ),
  Todo: (
    <SoftTag group="blue" style={tagStyle}>
      Booked
    </SoftTag>
  ),
  ReadyToGo: (
    <SoftTag group="blue" style={tagStyle}>
      Booked
    </SoftTag>
  ),
  Approved: (
    <SoftTag group="blue" style={tagStyle}>
      Booked
    </SoftTag>
  ),
  PartsNotOrdered: (
    <SoftTag group="yellow" style={tagStyle}>
      On hold
    </SoftTag>
  ),
  PartsOrdered: (
    <SoftTag group="yellow" style={tagStyle}>
      On hold
    </SoftTag>
  ),
};

export const ServiceOrderHistoryStatus = {
  Completed: 'Completed',
  Ready: 'Ready',
  Paid: 'Paid',
};

export type ServiceOrderHistoryStatusObjectKey = keyof typeof ServiceOrderHistoryStatus;
export type ServiceOrderStatusColorObjectKey = keyof typeof ServiceOrderStatusColor;
export type ServiceOrderStatusTagObjectKey = keyof typeof ServiceOrderStatusTag;
export type LocalizedServiceOrderStatusObjectKey = keyof typeof LocalizedServiceOrderStatus;
export type ServiceOrderStatusIconObjectKey = keyof typeof ServiceOrderStatusIcon;
export type ServiceOrderInvoiceStatusObjectKey = keyof typeof ServiceOrderInvoiceStatus;
export type DropOffDateStatusObjectKey = keyof typeof DropOffDateStatus;

/** The status tag of a job in a table: a draft is told in its words ("Draft Booking" / "Draft Estimate"), in the colour of its kind. */
export const serviceOrderStatusTagFor = (status: string, isDraft?: boolean) => {
  const tag = ServiceOrderStatusTag[status as ServiceOrderStatusTagObjectKey];
  return tag && isDraft ? React.cloneElement(tag, undefined, draftTagLabel(status)) : tag;
};

/**
 * The same tag, sized to its text.
 *
 * ServiceOrderStatusTag is padded to 200px so every tag in a table column lines up; outside a table
 * - in a panel header, next to a title - that padding is just dead space. Cloning keeps one source
 * of truth for the label and the colour.
 */
export const serviceOrderStatusTagCompact = (status: string) => {
  const tag = ServiceOrderStatusTag[status as ServiceOrderStatusTagObjectKey];
  if (!tag) return null;
  return React.cloneElement(tag, { style: { ...tag.props.style, minWidth: 0, margin: 0 } });
};
